import { useState, useEffect, useRef } from 'react';
import { ProductItem, ProductData } from '../types';
import { MaterialsService } from '../../../../api';
import { ColorsService } from '../../../../api';
import {
  correlationKeyFromItem,
  correlationKeyFromUnit,
  findCatalogProduct,
  isStrictlyAdditiveVersusSavedUnits,
  mergeUnitsAdditive,
  type MergedProductUnit,
} from '../productUnitsMerge.utils';

/** Catálogo → texto en formulario; sin dato = cadena vacía (nunca 'N/A' ni placeholders). */
function catalogFieldToString(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string' && value.trim() === '') return '';
  return String(value);
}

/** Normaliza id numérico (evita fallo por productId string vs number del API/formulario). */
function normalizeProductId(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return n;
}

/**
 * Comprueba si las unidades precargadas (p. ej. productUnits del padre) coinciden con
 * productos y cantidades del paso 1. Si el usuario volvió atrás y cambió líneas/cantidades,
 * devuelve false y debemos regenerar desde productItems.
 * Clave: productId (o code), nunca nombre.
 */
function initialSnapshotMatchesStep1(
  initialProductUnits: ProductUnit[] | undefined,
  productItems: ProductItem[],
): boolean {
  if (!initialProductUnits?.length) return false;

  const validItems = productItems.filter((item) => correlationKeyFromItem(item) != null);
  if (validItems.length === 0) return false;

  const expectedByKey = new Map<string, number>();
  for (const item of validItems) {
    const key = correlationKeyFromItem(item)!;
    const qty = Math.max(1, parseInt(String(item.quantity), 10) || 1);
    expectedByKey.set(key, (expectedByKey.get(key) ?? 0) + qty);
  }

  const actualByKey = new Map<string, number>();
  for (const u of initialProductUnits) {
    const key = correlationKeyFromUnit(u);
    if (!key) return false;
    actualByKey.set(key, (actualByKey.get(key) ?? 0) + 1);
  }

  if (expectedByKey.size !== actualByKey.size) return false;

  for (const [key, count] of expectedByKey) {
    if (actualByKey.get(key) !== count) return false;
  }
  return true;
}

export interface ProductUnit {
  id: string;
  productId: number;
  productName: string;
  productCode: string;
  quantity: number;
  properties: {
    // Hidden field used to stamp the step-1 supplier onto each unit payload.
    supplierId?: string;
    materialId: string; // Store material ID instead of material name
    materialName: string; // Keep material name for display
    weight: string;
    length: string;
    diameter: string;
    color: string;
    workshopCode: string;
  };
  costs: {
    costPrice: string;
    salePrice: string;
  };
}

/** Misma resolución que al generar unidades, usando datos de la fila. */
function findCatalogProductFromUnit(
  unit: ProductUnit,
  products: ProductData[],
): ProductData | undefined {
  const item: ProductItem = {
    id: 0,
    productName: unit.productName,
    productCode: unit.productCode,
    quantity: '1',
    productId: normalizeProductId(unit.productId as unknown),
  };
  return findCatalogProduct(item, products);
}

const emptyPropertiesFromReset = (supplierId: string | undefined) => ({
  supplierId: supplierId || '',
  materialId: '',
  materialName: '',
  weight: '',
  length: '',
  diameter: '',
  color: '',
  workshopCode: '',
});

const emptyCostsFromReset = () => ({
  costPrice: '',
  salePrice: '',
});

export interface ProductGroup {
  productName: string;
  productCode: string;
  productId: number;
  units: ProductUnit[];
  totalQuantity: number;
}

export interface ProductPropertiesStepProps {
  productItems: ProductItem[];
  products: ProductData[];
  selectedSupplierId?: string;
  onUpdateItem?: (id: number, field: keyof ProductItem, value: string) => void;
  onUpdateProperties?: (units: ProductUnit[]) => void;
  initialProductUnits?: ProductUnit[]; // Optional: pre-loaded units from PENDING records
}

export const useProductPropertiesStep = ({
  productItems,
  products,
  selectedSupplierId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onUpdateItem,
  onUpdateProperties,
  initialProductUnits,
}: ProductPropertiesStepProps) => {
  const [activeTab, setActiveTab] = useState<'properties' | 'costs'>('properties');
  const [units, setUnits] = useState<ProductUnit[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);

  const [materials, setMaterials] = useState<{ id: number; name: string }[]>([]);
  const [colors, setColors] = useState<{ id: number; name: string }[]>([]);
  // Track if we've already initialized from initialProductUnits to prevent infinite loops
  const hasInitializedFromProps = useRef(false);
  // Track the last initialProductUnits we used to detect record changes
  const lastInitialProductUnitsRef = useRef<ProductUnit[] | undefined>(undefined);
  // Skip first onUpdateProperties after loading from parent - prevents echo loop
  const skipNextSyncRef = useRef(false);
  // Track group keys to avoid unnecessary setExpandedProducts updates
  const prevGroupKeysRef = useRef<string>('');

  // Estado para productos expandidos/colapsados
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

  // Estado para columnas editables (switches) - por producto, por defecto todas editables
  const [editableColumns, setEditableColumns] = useState<
    Record<
      string,
      {
        materialId: boolean;
        weight: boolean;
        length: boolean;
        diameter: boolean;
        color: boolean;
        workshopCode: boolean;
        costPrice: boolean;
        salePrice: boolean;
      }
    >
  >({});

  // Toggle editable state for a column of a specific product
  const toggleColumnEditable = (
    productCode: string,
    column:
      | 'materialId'
      | 'weight'
      | 'length'
      | 'diameter'
      | 'color'
      | 'workshopCode'
      | 'costPrice'
      | 'salePrice',
  ) => {
    setEditableColumns((prev) => ({
      ...prev,
      [productCode]: {
        ...(prev[productCode] || {
          materialId: false,
          weight: false,
          length: false,
          diameter: false,
          color: false,
          workshopCode: false,
          costPrice: false,
          salePrice: false,
        }),
        [column]: !(prev[productCode]?.[column] ?? false),
      },
    }));
  };

  // Fetch materials
  const fetchMaterials = async () => {
    try {
      const materialsResponse = await MaterialsService.materialsControllerFindAll(1, 1000);
      if (materialsResponse && materialsResponse.data && Array.isArray(materialsResponse.data)) {
        setMaterials(materialsResponse.data);
      } else if (Array.isArray(materialsResponse)) {
        setMaterials(materialsResponse);
      } else {
        console.warn('Unexpected materials response format:', materialsResponse);
        setMaterials([]);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      setMaterials([]);
    }
  };

  const fetchColors = async () => {
    try {
      const colorsResponse = await ColorsService.colorsControllerFindAll(1, 1000);
      if (colorsResponse && colorsResponse.data && Array.isArray(colorsResponse.data)) {
        setColors(colorsResponse.data);
      } else if (Array.isArray(colorsResponse)) {
        setColors(colorsResponse);
      } else {
        console.warn('Unexpected colors response format:', colorsResponse);
        setColors([]);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
      setColors([]);
    }
  };

  // Initialize units from initialProductUnits (for PENDING records) or from productItems with parent product defaults
  useEffect(() => {
    const validProductItems = productItems.filter(
      (item) => (item.productName ?? '').trim() !== '' && correlationKeyFromItem(item) != null,
    );
    const snapshotMatches = initialSnapshotMatchesStep1(initialProductUnits, productItems);

    // Check if initialProductUnits represents a different record (compare by first unit's ID or length)
    const isDifferentRecord =
      lastInitialProductUnitsRef.current === undefined ||
      !initialProductUnits ||
      initialProductUnits.length === 0 ||
      lastInitialProductUnitsRef.current.length !== initialProductUnits.length ||
      (lastInitialProductUnitsRef.current.length > 0 &&
        initialProductUnits.length > 0 &&
        lastInitialProductUnitsRef.current[0]?.id !== initialProductUnits[0]?.id);

    // Reset initialization flag if we're switching from edit mode (had units) to create mode (empty units)
    const hadUnitsBefore =
      lastInitialProductUnitsRef.current && lastInitialProductUnitsRef.current.length > 0;
    const hasUnitsNow = initialProductUnits && initialProductUnits.length > 0;
    if (hadUnitsBefore && !hasUnitsNow) {
      // Switching from edit to create mode - reset the flag
      hasInitializedFromProps.current = false;
      lastInitialProductUnitsRef.current = undefined;
    }

    // Solo usar initialProductUnits del padre si reflejan el paso 1 (evita datos obsoletos tras volver atrás y cambiar cantidades)
    if (
      initialProductUnits &&
      initialProductUnits.length > 0 &&
      isDifferentRecord &&
      snapshotMatches
    ) {
      const normalizedInitialUnits = initialProductUnits.map((unit) => ({
        ...unit,
        properties: {
          ...unit.properties,
          diameter:
            unit.properties.diameter === 'N/A' || unit.properties.diameter === 'n/a'
              ? ''
              : unit.properties.diameter,
          // Keep supplier aligned with step 1 selection for assignment flow
          ...(selectedSupplierId ? { supplierId: selectedSupplierId } : {}),
        },
      }));
      setUnits(normalizedInitialUnits);
      hasInitializedFromProps.current = true;
      lastInitialProductUnitsRef.current = initialProductUnits;
      skipNextSyncRef.current = true; // Parent already has data; avoid echo loop
      return;
    }

    // Cambio solo aditivo (más filas o más cantidad): conservar unidades editadas y añadir nuevas con catálogo
    const additiveOnly =
      initialProductUnits &&
      initialProductUnits.length > 0 &&
      !snapshotMatches &&
      isStrictlyAdditiveVersusSavedUnits(productItems, initialProductUnits);

    if (additiveOnly && validProductItems.length > 0) {
      const merged = mergeUnitsAdditive(
        productItems,
        products,
        initialProductUnits as MergedProductUnit[],
        selectedSupplierId,
      );
      const normalizedMerged = merged.map((unit) => ({
        ...unit,
        properties: {
          ...unit.properties,
          diameter:
            unit.properties.diameter === 'N/A' || unit.properties.diameter === 'n/a'
              ? ''
              : unit.properties.diameter,
          ...(selectedSupplierId ? { supplierId: selectedSupplierId } : {}),
        },
      })) as ProductUnit[];
      setUnits(normalizedMerged);
      hasInitializedFromProps.current = true;
      lastInitialProductUnitsRef.current = normalizedMerged;
      skipNextSyncRef.current = true;
      return;
    }

    // Generar desde productItems: sin precarga, o precarga desfasada respecto al paso 1.
    // No exigir products.length > 0: con productId del paso 1 se crean unidades aunque el
    // cache local aún no tenga la ficha (defaults vacíos; nunca adivinar por nombre).
    const shouldGenerateFromItems =
      validProductItems.length > 0 && (!initialProductUnits?.length || !snapshotMatches);

    if (shouldGenerateFromItems && (!hasInitializedFromProps.current || !snapshotMatches)) {
      const initialUnits: ProductUnit[] = [];

      validProductItems.forEach((item) => {
        const product = findCatalogProduct(item, products);
        const quantity = parseInt(item.quantity) || 1;
        // Never invent productId 0 — identity must come from step 1 or catalog code.
        const resolvedProductId = normalizeProductId(item.productId) ?? product?.id;
        if (resolvedProductId == null) {
          return;
        }

        // Create individual units based on quantity with parent defaults
        for (let unitIndex = 0; unitIndex < quantity; unitIndex++) {
          initialUnits.push({
            id: `${item.productCode}-${unitIndex + 1}`,
            productId: resolvedProductId,
            productName: item.productName,
            productCode: item.productCode,
            quantity: 1,
            properties: {
              supplierId: selectedSupplierId || '',
              materialId: product?.material?.id ? product.material.id.toString() : '',
              materialName: product?.material?.name || '',
              weight: catalogFieldToString(product?.weightGrams),
              length: catalogFieldToString(product?.lengthMm),
              diameter: catalogFieldToString(product?.diameterSize),
              color: catalogFieldToString(product?.color),
              workshopCode: catalogFieldToString(product?.workshopCode),
            },
            costs: {
              costPrice: catalogFieldToString(product?.costPrice),
              salePrice: catalogFieldToString(product?.salePrice),
            },
          });
        }
      });

      setUnits(initialUnits);
      hasInitializedFromProps.current = true;
      lastInitialProductUnitsRef.current = initialUnits;
    }
  }, [productItems, products, initialProductUnits, selectedSupplierId]);

  // Fetch catalogs when component mounts
  useEffect(() => {
    fetchMaterials();
    fetchColors();
  }, []);

  // Group units by product
  useEffect(() => {
    const groups: ProductGroup[] = [];

    // Group units by product code
    const unitsByProduct = units.reduce(
      (acc, unit) => {
        if (!acc[unit.productCode]) {
          acc[unit.productCode] = [];
        }
        acc[unit.productCode].push(unit);
        return acc;
      },
      {} as Record<string, ProductUnit[]>,
    );

    // Preserve the order from productItems (step 1 order)
    // Create a map to track the order of products as they appear in productItems
    const productOrder = new Map<string, number>();
    productItems.forEach((item, index) => {
      if (!productOrder.has(item.productCode)) {
        productOrder.set(item.productCode, index);
      }
    });

    // Create product groups maintaining the original order
    // Sort by the order they appear in productItems
    const sortedProductCodes = Array.from(Object.keys(unitsByProduct)).sort((a, b) => {
      const orderA = productOrder.get(a) ?? Infinity;
      const orderB = productOrder.get(b) ?? Infinity;
      return orderA - orderB;
    });

    sortedProductCodes.forEach((productCode) => {
      const productUnits = unitsByProduct[productCode];
      if (productUnits.length > 0) {
        const firstUnit = productUnits[0];
        groups.push({
          productName: firstUnit.productName,
          productCode: firstUnit.productCode,
          productId: firstUnit.productId,
          units: productUnits,
          totalQuantity: productUnits.length,
        });
      }
    });

    setProductGroups(groups);

    // Only update expanded state when group structure changes (prevents unnecessary re-renders)
    const newGroupKeys = groups
      .map((g) => g.productCode)
      .sort()
      .join(',');
    if (newGroupKeys !== prevGroupKeysRef.current) {
      prevGroupKeysRef.current = newGroupKeys;
      const initialExpandedState: Record<string, boolean> = {};
      groups.forEach((group) => {
        initialExpandedState[group.productCode] = true;
      });
      setExpandedProducts(initialExpandedState);
    }
  }, [units, productItems]);

  // Notify parent when units change; skip after loading from parent to prevent echo loop
  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }
    if (onUpdateProperties != null && units.length > 0 && hasInitializedFromProps.current) {
      onUpdateProperties(units);
    }
  }, [units, onUpdateProperties]);

  // Toggle product expansion
  const toggleProductExpansion = (productCode: string) => {
    setExpandedProducts((prev) => ({
      ...prev,
      [productCode]: !prev[productCode],
    }));
  };

  // Reset units to parent defaults for a specific product
  const resetToParentDefaults = (productCode: string) => {
    const updatedUnits = units.map((unit) => {
      if (unit.productCode !== productCode) {
        return unit;
      }

      const product = findCatalogProductFromUnit(unit, products);

      if (product) {
        return {
          ...unit,
          productId: product.id,
          properties: {
            supplierId: selectedSupplierId || '',
            materialId: product.material?.id ? product.material.id.toString() : '',
            materialName: product.material?.name || '',
            weight: catalogFieldToString(product.weightGrams),
            length: catalogFieldToString(product.lengthMm),
            diameter: catalogFieldToString(product.diameterSize),
            color: catalogFieldToString(product.color),
            workshopCode: catalogFieldToString(product.workshopCode),
          },
          costs: {
            costPrice: catalogFieldToString(product.costPrice),
            salePrice: catalogFieldToString(product.salePrice),
          },
        };
      }

      return {
        ...unit,
        properties: emptyPropertiesFromReset(selectedSupplierId),
        costs: emptyCostsFromReset(),
      };
    });

    setUnits(updatedUnits);
  };

  // Funciones
  const handleUnitChange = (
    unitIndex: number,
    section: 'properties' | 'costs',
    field: string,
    value: string,
  ) => {
    const updatedUnits = [...units];

    // Special handling for materialId to also update materialName
    if (section === 'properties' && field === 'materialId') {
      const selectedMaterial = materials.find((m) => m.id.toString() === value);
      updatedUnits[unitIndex] = {
        ...updatedUnits[unitIndex],
        [section]: {
          ...updatedUnits[unitIndex][section],
          materialId: value,
          materialName: selectedMaterial?.name || '',
        },
      };
    } else {
      updatedUnits[unitIndex] = {
        ...updatedUnits[unitIndex],
        [section]: {
          ...updatedUnits[unitIndex][section],
          [field]: value,
        },
      };
    }

    setUnits(updatedUnits);
  };

  // Calcular margen
  const calculateMargin = (costPrice: string, salePrice: string) => {
    const cost = parseFloat(costPrice) || 0;
    const sale = parseFloat(salePrice) || 0;
    if (sale <= 0) return '0.00';
    return (((sale - cost) / sale) * 100).toFixed(2);
  };

  // Get unit index in global units array
  const getGlobalUnitIndex = (unit: ProductUnit) => {
    return units.findIndex((u) => u.id === unit.id);
  };

  return {
    // State
    activeTab,
    setActiveTab,
    units,
    productGroups,
    materials,
    colors,
    expandedProducts,
    editableColumns,

    // Functions
    toggleProductExpansion,
    toggleColumnEditable,
    resetToParentDefaults,
    handleUnitChange,
    calculateMargin,
    getGlobalUnitIndex,
  };
};
